import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Choose a hex code',
  error,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick(dropdownRef, () => setIsOpen(false));

  const predefinedColors = [
    '#621C43',
    '#4A082D',
    '#87074C',
    '#AD0C62',
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
    '#F8C471',
    '#82E0AA',
  ];

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const isValidHex = (color: string) => /^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(color);

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-1 text-[#621C43]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center gap-2 bg-[#4A082D33] rounded-lg border border-transparent focus-within:border-[#621C43]">
          <div
            className="w-8 h-8 ml-2 rounded border border-gray-300 cursor-pointer flex-shrink-0"
            style={{ backgroundColor: isValidHex(value) ? value : '#ffffff' }}
            onClick={() => setIsOpen(!isOpen)}
          />

          <input
            type="text"
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="flex-1 p-2 px-3 font-normal italic rounded-lg bg-transparent placeholder-[#621C43] focus:outline-none text-[#621C43]"
          />

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="mr-2 p-1 hover:bg-[#4A082D20] rounded"
          >
            <ChevronDown
              size={16}
              className={`text-[#621C43] transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          <input
            ref={colorInputRef}
            type="color"
            value={isValidHex(value) ? value : '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="absolute opacity-0 w-0 h-0"
          />
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#621C43] rounded-lg shadow-lg z-50 p-3">
            <div className="grid grid-cols-8 gap-2 mb-3">
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={`w-8 h-8 rounded border-2 hover:scale-110 transition-transform ${
                    value === color
                      ? 'border-[#621C43] ring-2 ring-[#621C43]/30'
                      : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>

            <div className="border-t border-gray-200 pt-3">
              <button
                type="button"
                onClick={() => colorInputRef.current?.click()}
                className="w-full p-2 text-sm text-[#621C43] bg-[#4A082D10] hover:bg-[#4A082D20] rounded-lg transition-colors"
              >
                Choose Custom Color
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-xs font-medium mt-1">{error}</p>}
    </div>
  );
};

export default ColorPicker;
